import { AutoComplete, AutoCompleteProps } from 'antd';
import { debounce, get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import FieldWithLabel from '../../../../common/components/Fields/FieldWithLabel';
import { FieldProps } from '../../../../common/components/Fields/types';
import { useAdminMarketsInOrderSearchLazyQuery } from '../../graphql/markets.generated';

type MarketAutoCompleteFieldProps<TFieldValues extends FieldValues> =
  AutoCompleteProps &
    FieldProps & {
      control: Control<TFieldValues>;
      name: Path<TFieldValues>;
    };

export default function MarketAutoCompleteField<
  TFieldValues extends FieldValues,
>({
  name,
  label,
  required,
  control,
  hideError,
  ...props
}: MarketAutoCompleteFieldProps<TFieldValues>) {
  const [fetchMarkets, { data }] = useAdminMarketsInOrderSearchLazyQuery();

  const fetchItems = useCallback(
    async (v: string) => {
      await fetchMarkets({
        variables: {
          filter: {
            name: v,
          },
          pagination: {
            take: 20,
            skip: 0,
          },
        },
      });
    },
    [fetchMarkets]
  );

  const handleSearch = useCallback(
    debounce(async (v: string) => {
      await fetchItems(v);
    }, 200),
    []
  );

  const options = useMemo(() => {
    return (data?.adminMarkets?.edges || []).map((edge) => {
      return {
        label: edge.node.name,
        value: edge.node.name,
        key: edge.node.id,
      };
    });
  }, [data]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;
        const status = !hideError && errorMessage ? 'error' : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <AutoComplete
              options={options}
              onSearch={handleSearch}
              className='w-full'
              status={status}
              optionRender={(oriOption) => {
                return (
                  <>
                    {oriOption.label}
                    <span className='ml-1 text-xs text-black/25'>
                      {oriOption.key}
                    </span>
                  </>
                );
              }}
              {...field}
              {...props}
            />

            {!hideError && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}
